import { graphqlClient } from '@/lib/graphql-client';
import { withUserScope } from '@/lib/user-scope';
import { omitScopeFields } from '@/lib/scope-stamp';
import { buildGetMbmProjectsLookupQuery, buildGetTimeSheetsQuery } from '../graphql/queries';
import {
  DELETE_TIME_SHEET_MUTATION,
  INSERT_TIME_SHEET_MUTATION,
  UPDATE_TIME_SHEET_MUTATION,
} from '../graphql/mutations';
import {
  GetTimeSheetsParams,
  MbmProjectOption,
  TimeSheetInsertInput,
  TimeSheetRecord,
  TimeSheetTask,
  TimeSheetUpdateInput,
  TimeSheetsPagingResult,
} from '../types/timesheet.types';

/**
 * TasksJson is stored as a JSON-serialized string on the TimeSheet schema
 * (server-side `IEnumerable<TaskResponseModel>`), so every read must
 * JSON.parse it into the domain TimeSheetTask[] shape -- same pattern as
 * BreakTimeDetails on Attendance (see hr-my-attendance/services/attendance.service.ts).
 */
const parseTasksJson = (raw: unknown): TimeSheetTask[] => {
  if (!raw || typeof raw !== 'string') return Array.isArray(raw) ? raw : [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const toTimeSheetRecord = (item: TimeSheetRecord & { TasksJson?: string }): TimeSheetRecord => ({
  ...item,
  Tasks: parseTasksJson(item.TasksJson),
});

export const getMyTimeSheets = async (
  params: GetTimeSheetsParams
): Promise<{ TimeSheets: TimeSheetsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  const result = (await graphqlClient.query({
    query: buildGetTimeSheetsQuery(filter, pageNo, pageSize, order),
  })) as { TimeSheets: TimeSheetsPagingResult };
  return {
    TimeSheets: {
      ...result.TimeSheets,
      items: result.TimeSheets.items.map(toTimeSheetRecord),
    },
  };
};

// NOTE: MbmProject.Status values weren't confirmed against a live catalog, so
// this intentionally doesn't filter by status -- all projects are offered in
// the picker rather than risking silently hiding valid ones on a guessed
// enum value.
export const getMbmProjectsLookup = async (): Promise<{
  MbmProjects: { items: MbmProjectOption[] };
}> => {
  const result = (await graphqlClient.query({
    query: buildGetMbmProjectsLookupQuery({}, 1, 500),
  })) as { MbmProjects: { items: MbmProjectOption[] } };
  return {
    MbmProjects: {
      items: [...result.MbmProjects.items].sort((a, b) =>
        a.ProjectName.localeCompare(b.ProjectName)
      ),
    },
  };
};

export const createTimeSheetEntry = async (input: TimeSheetInsertInput) => {
  const { Tasks, ...rest } = input;
  return graphqlClient.mutate({
    query: INSERT_TIME_SHEET_MUTATION,
    variables: {
      input: await withUserScope(rest.UserId, {
        ...rest,
        TasksJson: JSON.stringify(Tasks),
      }),
    },
  });
};

export const updateTimeSheetEntry = async (itemId: string, input: TimeSheetUpdateInput) => {
  const filter = JSON.stringify({ _id: itemId });
  const { Tasks, ...rest } = input;
  return graphqlClient.mutate({
    query: UPDATE_TIME_SHEET_MUTATION,
    variables: {
      filter,
      input: {
        ...omitScopeFields(rest),
        ...(Tasks ? { TasksJson: JSON.stringify(Tasks) } : {}),
      },
    },
  });
};

export const deleteTimeSheetEntry = async (itemId: string) => {
  const filter = JSON.stringify({ _id: itemId });
  return graphqlClient.mutate({
    query: DELETE_TIME_SHEET_MUTATION,
    variables: { filter, input: { isHardDelete: false } },
  });
};
